import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Check, ChevronDown, Filter, Minus, Search, X } from "lucide-react";
import { NONE_TOKEN } from "@/components/MultiSelect";

export default function CircuitFilter({ mainList = [], otherList = [], groups = null, counts = {}, value = [], onChange, testid = "filter-circuit" }) {
    const [open, setOpen] = useState(false);
    const [openGroups, setOpenGroups] = useState({}); // gi -> expanded
    const [shift, setShift] = useState(0); // px left-offset to keep the menu on screen
    const [query, setQuery] = useState("");
    const ref = useRef(null);
    const menuRef = useRef(null);

    useEffect(() => {
        if (!open) { setQuery(""); return undefined; }
        const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, [open]);

    // Keep the menu within the viewport: measure its natural position, then shift
    // it left/right so neither edge is clipped (mobile buttons sit near the screen edge).
    useLayoutEffect(() => {
        if (!open || !menuRef.current) { setShift(0); return; }
        const r = menuRef.current.getBoundingClientRect();
        const margin = 8;
        const natLeft = r.left - shift; // undo any shift already applied
        const natRight = r.right - shift;
        let s = 0;
        if (natRight > window.innerWidth - margin) s = (window.innerWidth - margin) - natRight;
        if (natLeft + s < margin) s = margin - natLeft;
        if (s !== shift) setShift(s);
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    const effGroups = groups || (mainList.length || otherList.length ? [{ label: "Circuits", items: [...mainList, ...otherList] }] : []);
    const all = effGroups.flatMap((g) => g.items);
    const real = value.filter((v) => v !== NONE_TOKEN);
    const showAll = value.length === 0;                        // default = everything checked
    const isNone = value.length === 1 && value[0] === NONE_TOKEN;
    const isChecked = (c) => (showAll ? true : real.includes(c));
    const allSelected = showAll || (all.length > 0 && all.every((c) => real.includes(c)));

    const commit = (next) => {
        const uniq = [...new Set(next)];
        if (all.length > 0 && uniq.length === all.length && all.every((x) => uniq.includes(x))) { onChange([]); return; }
        if (uniq.length === 0) { onChange([NONE_TOKEN]); return; }
        onChange(uniq);
    };
    const base = () => (showAll ? all.slice() : real.slice());
    const toggle = (c) => {
        const b = base();
        commit(b.includes(c) ? b.filter((x) => x !== c) : [...b, c]);
    };
    const toggleGroup = (items) => {
        const b = base();
        const full = items.length > 0 && items.every((i) => b.includes(i));
        commit(full ? b.filter((i) => !items.includes(i)) : [...b, ...items]);
    };
    const toggleAll = () => (allSelected ? onChange([NONE_TOKEN]) : onChange([]));

    const displayText =
        showAll ? "Circuits" :
        isNone || real.length === 0 ? "None" :
        real.length === 1 ? real[0] :
        `${real.length} selected`;
    const isDefault = showAll;

    const Box = ({ state }) => (
        <span className={`h-4 w-4 border flex items-center justify-center flex-none ${state === "none" ? "border-[#3a3a3a] bg-transparent" : "bg-[#ff4500] border-[#ff4500]"}`}>
            {state === "all" && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
            {state === "some" && <Minus className="h-3 w-3 text-white" strokeWidth={3} />}
        </span>
    );

    const CircuitRow = ({ name, indent = true }) => (
        <button
            type="button"
            data-testid={`${testid}-option-${name}`}
            onClick={() => toggle(name)}
            className={`w-full flex items-center gap-2 ${indent ? "pl-8" : "pl-3"} pr-3 py-2 text-sm text-left text-white hover:bg-[#1a1a1a] cursor-pointer whitespace-nowrap`}
        >
            <Box state={isChecked(name) ? "all" : "none"} />
            <span className="flex-1">{name}</span>
            <span className="ml-6 text-[11px] text-[#666] tabular-nums flex-none" data-testid={`${testid}-count-${name}`}>{counts[name] || 0}</span>
        </button>
    );

    const q = query.trim().toLowerCase();
    const matches = q ? all.filter((c) => c.toLowerCase().includes(q)) : [];

    return (
        <div className="relative inline-block" ref={ref}>
            <button
                type="button"
                data-testid={testid}
                onClick={() => setOpen((o) => !o)}
                className="w-auto h-11 rounded-none bg-[#141414] border border-[#2a2a2a] hover:border-[#ff4500] transition-colors flex items-center gap-1.5 px-2.5 text-sm text-left"
            >
                <Filter className={`h-3.5 w-3.5 shrink-0 ${isDefault ? "text-[#a0a0a0]" : "text-[#ff4500]"}`} />
                <span className={`whitespace-nowrap ${isDefault ? "" : "text-[#ff4500]"}`}>{displayText}</span>
                <ChevronDown className={`h-3.5 w-3.5 shrink-0 transition-transform ${open ? "rotate-180" : ""} ${isDefault ? "text-[#a0a0a0]" : "text-[#ff4500]"}`} />
            </button>

            {open && (
                <div ref={menuRef} data-testid={`${testid}-menu`} style={{ scrollbarGutter: "stable", left: shift }} className="absolute z-[9999] mt-1 w-max min-w-full max-w-[calc(100vw-1rem)] bg-[#141414] border border-[#2a2a2a] shadow-xl max-h-[70vh] overflow-y-auto overflow-x-hidden">
                    <div className="sticky top-0 z-10 bg-[#141414] border-b border-[#2a2a2a] p-2">
                        <div className="flex items-center gap-2 bg-[#0f0f0f] border border-[#2a2a2a] px-2 h-9">
                            <Search className="h-3.5 w-3.5 shrink-0 text-[#666]" />
                            <input
                                type="text"
                                data-testid={`${testid}-search`}
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder="Search circuits…"
                                className="flex-1 min-w-0 bg-transparent text-sm text-white placeholder:text-[#666] outline-none"
                            />
                            {query && (
                                <button type="button" aria-label="Clear search" data-testid={`${testid}-search-clear`} onClick={() => setQuery("")} className="shrink-0 text-[#666] hover:text-white">
                                    <X className="h-3.5 w-3.5" />
                                </button>
                            )}
                        </div>
                    </div>

                    {q ? (
                        matches.length ? (
                            matches.map((c) => <CircuitRow key={c} name={c} indent={false} />)
                        ) : (
                            <div className="px-3 py-4 text-sm text-[#666] whitespace-nowrap" data-testid={`${testid}-no-matches`}>No circuits match “{query.trim()}”</div>
                        )
                    ) : (
                    <>
                    <button
                        type="button"
                        data-testid={`${testid}-select-all`}
                        onClick={toggleAll}
                        className="w-full flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-wider text-white bg-[#0f0f0f] hover:bg-[#1a1a1a] border-b border-[#2a2a2a] whitespace-nowrap"
                    >
                        <Box state={allSelected ? "all" : (real.length ? "some" : "none")} />
                        Select All
                    </button>

                    {effGroups.map((g, gi) => {
                        const items = g.items;
                        const nSel = items.filter((c) => isChecked(c)).length;
                        const gState = nSel === 0 ? "none" : nSel === items.length ? "all" : "some";
                        const gCount = items.reduce((s, c) => s + (counts[c] || 0), 0);
                        const expanded = !!openGroups[gi];
                        return (
                            <div key={`${g.label}-${gi}`} data-testid={`${testid}-group-${gi}`} className="border-b border-[#1c1c1c]">
                                <div className="flex items-stretch bg-[#0c0c0c]">
                                    <button
                                        type="button"
                                        aria-label={`Select all ${g.label}`}
                                        data-testid={`${testid}-group-toggle-${gi}`}
                                        onClick={() => toggleGroup(items)}
                                        className="flex items-center pl-3 pr-1 py-2 hover:bg-[#161616]"
                                    >
                                        <Box state={gState} />
                                    </button>
                                    <button
                                        type="button"
                                        aria-label={`Expand ${g.label}`}
                                        data-testid={`${testid}-group-expand-${gi}`}
                                        onClick={() => setOpenGroups((o) => ({ ...o, [gi]: !o[gi] }))}
                                        className="flex-1 min-w-0 flex items-center gap-1.5 pl-1 pr-3 py-2 text-left text-white hover:bg-[#161616] whitespace-nowrap"
                                    >
                                        <span className="text-[13px] uppercase tracking-wide font-semibold">{g.label}</span>
                                        <ChevronDown className={`h-4 w-4 flex-none transition-transform ${expanded ? "rotate-180" : ""} text-[#a0a0a0]`} />
                                        <span className="flex-1" />
                                        <span className="text-[11px] text-[#666] tabular-nums flex-none">{gCount}</span>
                                    </button>
                                </div>
                                {expanded && (
                                    <>
                                        <button
                                            type="button"
                                            data-testid={`${testid}-group-select-all-${gi}`}
                                            onClick={() => toggleGroup(items)}
                                            className="w-full flex items-center gap-2 pl-8 pr-3 py-2 text-[11px] uppercase tracking-wider text-[#a0a0a0] hover:bg-[#1a1a1a] cursor-pointer whitespace-nowrap border-b border-[#1c1c1c]"
                                        >
                                            <Box state={gState} />
                                            <span className="flex-1 text-left">Select All</span>
                                        </button>
                                        {items.map((c) => <CircuitRow key={c} name={c} />)}
                                    </>
                                )}
                            </div>
                        );
                    })}
                    </>
                    )}
                </div>
            )}
        </div>
    );
}
